import {
    Alert,
    Button,
    Col,
    ControlLabel,
    FormGroup,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader, OverlayTrigger,
    Radio,
    Row, Tooltip,
    Well
} from "react-bootstrap";
import React, {useEffect, useState} from 'react'
import MDSpinner from "react-md-spinner";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faLongArrowAltLeft, faLongArrowAltRight} from "@fortawesome/free-solid-svg-icons";
import ErrorInfoModal from "./ErrorInfoModal";
import MatchedCustomersModal from "./MatchedCustomersModal";
import * as _ from 'lodash';
import useModalKeyboardNavigation from "../../utilities/hooks/useModalKeyboardNavigation";

const formatAddress = address => {
    if (!address) return "";

    return _.compact([
        address.address_1,
        address.address_2,
        address.city,
        address.state,
        address.zip,
        address.country
    ]).join(", ");
}

const siteErrorMessage = siteError => {
    const mismatches = siteError.address_mismatches || [];
    if (mismatches[0] && mismatches[0].message) {
        return mismatches[0].message;
    }
    if (mismatches.length > 0) {
        return "Address mismatch detected. Use Show address mismatches.";
    }

    return siteError.error_message;
};

const normalizeSyncSiteError = site => ({
    ...site,
    address_mismatches: (site?.address_mismatches || [])
});

const normalizeSyncError = error => ({
    ...error,
    address_mismatches: (error?.address_mismatches || []),
    error_sites: (error.error_sites || []).map(normalizeSyncSiteError)
});

const normalizeSyncErrors = errors => (errors || []).map(normalizeSyncError);
const SYNC_MODAL_NAVIGATION_SELECTOR = "button:not([disabled]):not(.close), input[type='radio']:not([disabled])";

const SyncAllModal = (props) => {


    const synchronizeRecords = (importId) => {
        const {
            category,
            to,
            from,
            byInvoice,
            shippingAddressSync,
            page,
            perPage,
            showNotSyncCustomer,
            showSynchronized
        } = props
        setExpandedMismatchGroups({});
        setLoadingMismatchGroups({});
        let progressTimer = setInterval(() => props.checkProgress(importId, (result) => {
            setTotal(result.total)
            setProgress(result.current)
            setStatus(result.status)
        }), 3000);
        const filters = {
            from,
            to,
            byInvoice,
            shippingAddressSync,
            page,
            per_page: perPage,
            showNotSyncCustomer,
            showSynchronized
        }
        props.synchronizeManyRecords(category, choice, importId, filters, (res) => {
            clearInterval(progressTimer);
            props.checkProgress(importId, (result) => {
                setProgress(result.current)
                setStatus(result.status)
                setLoading(false)
            })
            props.reload()
            setProgressTable(res?.progress)
            setNotFullySyncTable(res?.not_fully_sync)
            setErrorsTable(normalizeSyncErrors(res?.errors))
            if (res?.matched_customer && res?.matched_customer.length > 0) {
                setMatchedCustomer({show: true, customers: res?.matched_customer})
            }
        }, (res) => {
            clearInterval(progressTimer);
            setStatus('failed')
            setLoading(false)
            if (res?.data?.message) {
                setErrorModal({show: true, message: res?.data?.message})
            }
        })
    }

    const dateRangeMoreThanMonth = (from, to) => {
        return to.diff(from, 'days') > 30
    }

    const mismatchIdentity = mismatch => `${mismatch?.record_type || ''}:${mismatch?.quickbooks_id || ''}`;

    const mismatchList = source => _.uniqBy(source.address_mismatches || [], mismatchIdentity);

    const isCustomerMismatch = mismatch => mismatch?.record_type === "customer";
    const isDetailedMismatch = mismatch => !!(mismatch?.current_address || mismatch?.proposed_address || mismatch?.mismatches?.length);
    const canAcceptMismatch = mismatch => !!(["site", "customer"].includes(mismatch?.record_type) && mismatch?.quickbooks_id);

    const siteErrorKey = siteError => {
        return siteError?.site_name || mismatchList(siteError)[0]?.quickbooks_id || '';
    };

    const buildPreviewPayload = customerMismatch => ({
        record_type: customerMismatch.record_type,
        quickbooks_id: customerMismatch.quickbooks_id
    });

    const mergeSiteMismatches = (existingSites, previewData) => {
        const mergedSites = [...(existingSites || [])];
        const previewSites = (previewData.error_sites || []).filter(site => mismatchList(site).length > 0);
        const syntheticPreviewSites = mismatchList(previewData)
            .filter(mismatch => mismatch?.record_type === "site")
            .map(mismatch => ({
                site_name: mismatch?.quickbooks_id || "Site",
                address_mismatches: [mismatch]
            }));
        const allPreviewSites = _.uniqBy(
            [...previewSites, ...syntheticPreviewSites],
            site => `${siteErrorKey(site)}:${mismatchIdentity(mismatchList(site)[0])}`
        );

        allPreviewSites.forEach(previewSite => {
            const key = siteErrorKey(previewSite);
            const foundIndex = mergedSites.findIndex(site => siteErrorKey(site) === key);

            if (foundIndex >= 0) {
                const combinedSiteMismatches = _.uniqBy(
                    [...mismatchList(previewSite), ...mismatchList(mergedSites[foundIndex])],
                    mismatchIdentity
                );
                mergedSites[foundIndex] = {
                    ...mergedSites[foundIndex],
                    address_mismatches: combinedSiteMismatches
                };
            } else {
                mergedSites.push({
                    ...previewSite,
                    address_mismatches: mismatchList(previewSite)
                });
            }
        });

        return mergedSites;
    };

    const mergePreviewIntoError = (errorIndex, previewData, options = {}) => {
        const {excludeCustomerMismatchId = null} = options;

        setErrorsTable(prev => prev.map((error, index) => {
            if (index !== errorIndex) return error;

            const customerMismatches = _.uniqBy(
                [
                    ...mismatchList(previewData)
                        .filter(isCustomerMismatch)
                        .filter(mismatch => !excludeCustomerMismatchId || mismatchIdentity(mismatch) !== excludeCustomerMismatchId),
                    ...mismatchList(error)
                        .filter(isCustomerMismatch)
                        .filter(mismatch => !excludeCustomerMismatchId || mismatchIdentity(mismatch) !== excludeCustomerMismatchId)
                ],
                mismatchIdentity
            );

            const mergedSites = mergeSiteMismatches(error.error_sites, previewData);

            return {
                ...error,
                address_mismatches: customerMismatches,
                error_sites: mergedSites
            };
        }));
    };

    const removeAcceptedAddressMismatch = (errorIndex, mismatch, siteIndex = null) => {
        const acceptedId = mismatchIdentity(mismatch);
        const finalizeResolvedError = (nextError, previousError) => {
            const isResolved = mismatchList(nextError).length === 0
                && !nextError.errors?.length
                && !nextError.error_sites?.length;
            if (!isResolved) {
                return nextError;
            }

            const label = previousError?.client_name;
            if (label) {
                setProgressTable(prevProgress => _.uniq([...(prevProgress || []), label]));
            }

            return null;
        };

        setErrorsTable(prev =>
            prev
                .map((error, index) => {
                    if (index !== errorIndex) return error;

                    if (siteIndex === null) {
                        const currentMismatches = mismatchList(error);
                        const nextMismatches = currentMismatches.filter(item => mismatchIdentity(item) !== acceptedId);

                        const nextError = {
                            ...error,
                            address_mismatches: nextMismatches,
                            errors: (error.errors || []).filter(e => e !== mismatch?.message)
                        };

                        return finalizeResolvedError(nextError, error);
                    }

                    const errorSites = (error.error_sites || [])
                        .map((site, i) => {
                            if (i !== siteIndex) return site;

                            const siteMismatches = mismatchList(site).filter(item => mismatchIdentity(item) !== acceptedId);
                            return {
                                ...site,
                                address_mismatches: siteMismatches
                            };
                        })
                        .filter(site => site.error_message || mismatchList(site).length > 0);

                    const nextError = {...error, error_sites: errorSites};
                    return finalizeResolvedError(nextError, error);
                })
                .filter(Boolean)
        );
    };

    const acceptAddressMismatch = (mismatch, errorIndex, siteIndex = null, mismatchIndex = 0) => {
        const key = `${mismatchIdentity(mismatch)}:${mismatchIndex}`;

        setSavingAddressMismatch(key);
        props.acceptAddressMismatch({
                record_type: mismatch.record_type,
                record_id: mismatch.record_id,
                address: mismatch.proposed_address,
                s_address_sync: mismatch.s_address_sync,
                quickbooks_id: mismatch.quickbooks_id,
                customer_quickbooks_id: mismatch.customer_quickbooks_id
            },
            (responseData) => {
                const returnedData = responseData || {};
                const returnedMismatches = mismatchList(returnedData);

                removeAcceptedAddressMismatch(errorIndex, mismatch, siteIndex);
                if (siteIndex === null && (returnedMismatches.length > 0 || returnedData?.error_sites)) {
                    mergePreviewIntoError(errorIndex, returnedData, {
                        excludeCustomerMismatchId: mismatchIdentity(mismatch)
                    });
                }
                setSavingAddressMismatch(null);
                props.reload();
            },
            (res) => {
                const returnedMismatches = mismatchList(res?.data || {});
                setSavingAddressMismatch(null);
                if (siteIndex === null && (returnedMismatches.length > 0 || res?.data?.error_sites)) {
                    mergePreviewIntoError(errorIndex, res?.data || {}, {
                        excludeCustomerMismatchId: mismatchIdentity(mismatch)
                    });
                    return;
                }
                setErrorModal({
                    show: true,
                    message: res?.data?.message || returnedMismatches[0]?.message
                })
            }
        )
    }

    const addressMismatchDetails = (mismatch, errorIndex, options = {}) => {
        const {siteIndex = null, mismatchIndex = 0, disableAccept = false} = options;
        const key = `${mismatchIdentity(mismatch)}:${mismatchIndex}`;

        return (
            <div className="margin-top-20" style={{borderLeft: '3px solid #ddd', marginLeft: 10, paddingLeft: 12}}>
                <div className='width-90'>
                    <Row>
                        <Col xs={6}>
                            <b>Current address</b>
                            <p>{formatAddress(mismatch.current_address)}</p>
                        </Col>
                        <Col xs={6}>
                            <b>Proposed address</b>
                            <p>{formatAddress(mismatch.proposed_address)}</p>
                        </Col>
                    </Row>
                    {mismatch.mismatches?.map((item, index) => (
                        <Row key={`${item.field}-${index}`}>
                            <Col xs={4}>{index + 1}. {item.field}:</Col>
                            <Col xs={4}>Current: {item.got}</Col>
                            <Col xs={4}>Proposed: {item.expected}</Col>
                        </Row>
                    ))}
                </div>
                <div className="text-right margin-top-20">
                    <Button
                        bsSize="xsmall"
                        bsStyle="success"
                        disabled={
                            savingAddressMismatch === key
                            || !props.acceptAddressMismatch || disableAccept || !canAcceptMismatch(mismatch)
                        }
                        onClick={() => acceptAddressMismatch(mismatch, errorIndex, siteIndex, mismatchIndex)}
                    >
                        Accept proposed address
                    </Button>
                </div>
                {disableAccept && (
                    <p className="text-muted">Accept customer address first to enable site sync.</p>
                )}
            </div>
        )
    }

    const customerMismatchDetails = (error, errorIndex) => {
        return mismatchList(error).filter(isDetailedMismatch).map((mismatch, mismatchIndex) => (
            <div key={`${mismatchIdentity(mismatch)}:${mismatchIndex}`}>
                {addressMismatchDetails(mismatch, errorIndex, {mismatchIndex})}
            </div>
        ));
    };

    const siteMismatchDetails = (siteError, error, errorIndex, siteIndex) => {
        const hasPendingCustomerMismatch = mismatchList(error).some(
            mismatch => isCustomerMismatch(mismatch) && canAcceptMismatch(mismatch)
        );

        return mismatchList(siteError).filter(isDetailedMismatch).map((mismatch, mismatchIndex) => (
            <div key={`${mismatchIdentity(mismatch)}:${mismatchIndex}`}>
                {addressMismatchDetails(mismatch, errorIndex, {
                    siteIndex,
                    mismatchIndex,
                    disableAccept: hasPendingCustomerMismatch
                })}
            </div>
        ));
    };

    const toggleMismatchGroup = (key, error, errorIndex) => {
        const shouldOpen = !expandedMismatchGroups[key];

        setExpandedMismatchGroups(prev => ({
            ...prev,
            [key]: !prev[key]
        }))

        if (!shouldOpen || !props.previewAddressMismatches) {
            setLoadingMismatchGroups(prev => ({
                ...prev,
                [key]: false
            }));
            return;
        }

        const customerMismatch = mismatchList(error).find(isCustomerMismatch);
        const siteMismatch = (error.error_sites || [])
            .flatMap(site => mismatchList(site))
            .find(mismatch => mismatch.record_type === "site");
        const previewMismatch = customerMismatch || siteMismatch;

        if (!previewMismatch) {
            setLoadingMismatchGroups(prev => ({
                ...prev,
                [key]: false
            }));
            return;
        }

        setLoadingMismatchGroups(prev => ({
            ...prev,
            [key]: true
        }));

        props.previewAddressMismatches(
            buildPreviewPayload(previewMismatch),
            res => {
                mergePreviewIntoError(errorIndex, res || {});
                setLoadingMismatchGroups(prev => ({
                    ...prev,
                    [key]: false
                }));
            },
            () => {
                setLoadingMismatchGroups(prev => ({
                    ...prev,
                    [key]: false
                }));
            }
        );
    };

    const alertRender = () => {
        let alertText
        let additionalText = undefined
        let notFullySyncText = undefined
        if (props.category === 'customers' && dateRangeMoreThanMonth(props.from, props.to)) {
            alertText = `Please narrow date filter search to one month or less`
        } else if (status === 'completed') {
            alertText = progressTable && progressTable.length > 0 ? `Successfully imported ${progressTable.length} ${props.category}` : undefined
            notFullySyncText = notFullySyncTable && notFullySyncTable.length > 0 ? `Successfully imported ${notFullySyncTable.length} ${props.category}, but with some mismatches` : undefined
            additionalText = errorsTable && errorsTable.length > 0 ? `Unsuccessfully imported ${errorsTable.length} ${props.category}` : undefined
        } else if (status === 'failed') {
            alertText = `Import error occurred. \n
            Unsuccessfully imported ${total - progress || 0} ${props.category}`
        } else {
            alertText = `You are about to export/import ${props.category}.
            Please make sure that you have a stable internet connection as it will take some time.`
        }
        return (
            <>
                {alertText &&
                    <Alert bsStyle={status === 'completed' ? "success" : status === 'failed' ? "danger" : "warning"}>
                        <p>{alertText}</p>
                        {progressTable?.map(el => <p>{el}</p>)}
                    </Alert>}
                {notFullySyncText && (
                    <Alert bsStyle="warning">
                        <p>{notFullySyncText}</p>
                        {notFullySyncTable.map((el, index) => {
                            return (
                                <Row>
                                    <Col xs={12} className="text-left">
                                        <b>{el.client_name}</b>
                                        {(el.errors || []).map(e => {
                                            const {site} = e
                                            return (
                                                <p style={{display: "flex", justifyContent: "space-between", alignContent: "center"}}>
                                                    {e.error}
                                                    {site && (
                                                        <OverlayTrigger placement="right" overlay={<Tooltip id="tooltip">Sync site</Tooltip>}>
                                                            <FontAwesomeIcon className="pointer" icon={faLongArrowAltLeft} onClick={() => props.synchronizeCustomer(site.id, site.quickbooks_id, site.customer_quickbooks_id)} />
                                                        </OverlayTrigger>
                                                    )}
                                                </p>
                                            )
                                        })}

                                    </Col>
                                    <Col xs={12}>
                                        <hr/>
                                    </Col>
                                </Row>
                            )
                        })}
                    </Alert>
                )}
                {additionalText && <Alert bsStyle={"danger"}>
                    <p>{additionalText}</p>
                    {errorsTable?.map((el, errorIndex) => {
                        const groupKey = `customer-${errorIndex}`;
                        const expandedGroup = !!expandedMismatchGroups[groupKey];
                        const hasMismatchGroup = mismatchList(el).length > 0 || (el.error_sites || []).some(site => mismatchList(site).length > 0);
                        const mismatchGroupLoading = !!loadingMismatchGroups[groupKey];

                        return <Row>
                            <Col xs={12} className="text-left">
                                <b>{el.client_name}</b>
                                {(el.errors || []).map(e => <p>{e}</p>)}
                                {hasMismatchGroup && (
                                    <div className="margin-top-20">
                                        <Button bsSize="xsmall"
                                                onClick={() => toggleMismatchGroup(groupKey, el, errorIndex)}>
                                            {expandedGroup ? 'Hide address mismatches' : 'Show address mismatches'}
                                        </Button>
                                    </div>
                                )}
                                {expandedGroup && mismatchGroupLoading && (
                                    <p className="margin-top-20">Loading address mismatches...</p>
                                )}
                                {expandedGroup && customerMismatchDetails(el, errorIndex)}
                            </Col>
                            {expandedGroup && el.error_sites?.length > 0 && (
                                <Col xs={11} xsOffset={1}>
                                    {el.error_sites.map((e, siteIndex) => {
                                        return <Row className='margin-top-20'>
                                            <Col xs={12}>
                                                <p>{siteErrorMessage(e)}</p>
                                                {siteMismatchDetails(e, el, errorIndex, siteIndex)}
                                            </Col>
                                        </Row>
                                    })}
                                </Col>
                            )}
                            <Col xs={12}>
                                <hr/>
                            </Col>
                        </Row>
                    })}
                </Alert>}
            </>
        )
    }


    const [choice, setChoice] = useState('right')
    const [loading, setLoading] = useState(true)
    const [progress, setProgress] = useState(null)
    const [status, setStatus] = useState(null)
    const [total, setTotal] = useState(null)
    const [progressTable, setProgressTable] = useState([])
    const [notFullySyncTable, setNotFullySyncTable] = useState([])
    const [errorsTable, setErrorsTable] = useState([])
    const [errorModal, setErrorModal] = useState({show: false, message: ''})
    const [matchedCustomer, setMatchedCustomer] = useState({show: false, customers: []})
    const [savingAddressMismatch, setSavingAddressMismatch] = useState(null)
    const [expandedMismatchGroups, setExpandedMismatchGroups] = useState({})
    const [loadingMismatchGroups, setLoadingMismatchGroups] = useState({})

    const keyboardNavigationEnabled = props.show && !errorModal?.show && !matchedCustomer?.show
    const syncNotAllowed = props.category === 'customers' && dateRangeMoreThanMonth(props.from, props.to)

    const startSync = () => {
        if (syncNotAllowed) return;
        setLoading(true)
        setTotal(0)
        props.createImport((result) => {
            setStatus(result.status)
            synchronizeRecords(result.id)
        })
    }

    useEffect(() => {
        setLoading(false)
    }, [])

    useModalKeyboardNavigation({
        modalId: "qb-import-modal",
        enabled: keyboardNavigationEnabled,
        buttonSelector: SYNC_MODAL_NAVIGATION_SELECTOR,
        dependencies: [
            loading,
            errorsTable,
            savingAddressMismatch,
            expandedMismatchGroups,
            loadingMismatchGroups,
            syncNotAllowed
        ]
    })

    return <Modal
        show={props.show}
        bsSize="large"
        backdrop
        animation
        onHide={() => {
            if (!loading) {
                props.onHide()
            }
        }}
        id='qb-import-modal'
    >
        <ModalHeader closeButton>
            Synchronize all {props.category}
        </ModalHeader>
        <ModalBody>
            <Row className="align-center marginBottom10 marginTop-16">Choose direction:</Row>
            <Row>
                <Col md={6} className="align-right">
                    <FormGroup>
                        <ControlLabel>Import from Quickbooks</ControlLabel>
                        <Radio
                            disabled={loading}
                            name="choice"
                            checked={choice === 'left'}
                            onChange={() => {
                                setStatus(null)
                                setChoice('left')
                                setProgressTable([])
                                setNotFullySyncTable([])
                                setErrorsTable([])
                            }}
                            value={'left'}
                        />
                    </FormGroup>
                </Col>
                <Col md={6} className="align-left">
                    <FormGroup>
                        <ControlLabel>Export to Quickbooks</ControlLabel>
                        <Radio
                            disabled={loading}
                            name="choice"
                            checked={choice === 'right'}
                            onChange={() => {
                                setStatus(null)
                                setChoice('right')
                                setProgressTable([])
                                setNotFullySyncTable([])
                                setErrorsTable([])
                            }}
                            value={'right'}
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Well bsSize="large" className="align-center">
                    {loading ?
                        <div>
                            {status === 'in_progress' && total === 500 &&
                                <Alert bsStyle={'info'}>
                                    The number of {props.category} to import has been exceeded (maximum is 500 per
                                    action). Retry the operation after it is completed
                                </Alert>}
                            Please wait...
                            <br/>
                            <MDSpinner className="mt-20"/>
                            {status === 'in_progress' &&
                                <div className="mt-20">
                                    {progress}/{total}
                                </div>}
                        </div>
                        :
                        <div>
                            {alertRender()}
                            {choice === 'left' &&
                                <div>
                                    You can import {props.category} from Quickbooks that don't yet exist in
                                    Treehub.<br/>
                                    <br/>
                                    <button
                                        type="button"
                                        className={`clean-button pointer ${syncNotAllowed && 'arrow-disabled'}`}
                                        onClick={startSync}
                                        disabled={syncNotAllowed}
                                    >
                                        <FontAwesomeIcon
                                            icon={faLongArrowAltLeft}
                                            size="4x"
                                        />
                                    </button>
                                </div>
                            }
                            {choice === 'right' &&
                                <div>
                                    You can export {props.category} from Treehub that don't yet exist in
                                    Quickbooks.<br/>
                                    <br/>
                                    <button
                                        type="button"
                                        className={`clean-button pointer ${syncNotAllowed && 'arrow-disabled'}`}
                                        onClick={startSync}
                                        disabled={syncNotAllowed}
                                    >
                                        <FontAwesomeIcon
                                            icon={faLongArrowAltRight}
                                            size="4x"
                                        />
                                    </button>
                                </div>}
                        </div>
                    }
                </Well>
                {errorModal?.show &&
                    <ErrorInfoModal
                        type="Customer"
                        message={errorModal?.message}
                        onHide={() => setErrorModal({show: false, message: ''})}
                    />
                }
                {matchedCustomer.show &&
                    <MatchedCustomersModal
                        matchedCustomers={matchedCustomer.customers}
                        onHide={() => {
                            const matchedCustomerModal = {
                                show: false,
                                customers: []
                            }
                            setMatchedCustomer(matchedCustomerModal)
                        }}
                        reload={props.reload}
                        onSync={props.synchronizeSelectedCustomer}
                    />
                }
            </Row>

        </ModalBody>
        <ModalFooter>
            <Button disabled={loading} onClick={() => props.onHide()}>Close</Button>
        </ModalFooter>
    </Modal>
}

export default SyncAllModal
